import React from "react";
import { styled } from "@mui/system";
import { ITimePeriodOption } from "../defaults";
import { capitalize } from "lodash";
import DownArrowIcon from "src/icons/portfolio/DownArrowIcon";
interface ClickedProps {
  clicked: boolean;
}
export const TimePeriodDropdownWrapper = styled("div")<ClickedProps>(({ clicked, theme }) => ({
  display: "flex",
  padding: "4px 8px",
  height: "22px",
  alignItems: "center",
  gap: "4px",
  borderRadius: "20px",
  border: `1px solid ${theme.palette.primary.main}`,
  cursor: "pointer",
  // conditional
  // smooth background transition
  transition: "background-color 0.3s ease",
  backgroundColor: clicked ? theme.palette.primary.main : `#fff`,

  "&:hover": {
    backgroundColor: clicked ? theme.palette.primary.main : theme.palette.primary.contrastText,
  },
}));

export const DropdownTitle = styled("div")<ClickedProps>(({ clicked, theme }) => ({
  fontSize: "14px",
  color: clicked ? `#fff` : theme.palette.primary.main,
  userSelect: "none",
}));

export const DropdownContentWrapper = styled("div")({
  position: "absolute",
});

interface DropdownContentProps {
  width: string;
  top: number;
  left: number;
}

export const DropdownContent = styled("div")<DropdownContentProps>(
  ({ width, top, left, theme }) => ({
    display: "inline-block",
    flexDirection: "column",
    alignItems: "flex-start",
    position: "relative",
    top: top,
    left: left,
    width: width,
    padding: "12px 0px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0px 12px 24px 0px rgba(216, 216, 218, 0.80)",
    borderRadius: "0px 0px 8px 8px",
    zIndex: 200,
  }),
);

export const ContentOption = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "8px 12px",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  userSelect: "none",

  "&:hover": {
    background: theme.palette.primary.contrastText,
  },
}));

export const DropdownArrowIcon = styled("div")<ClickedProps>(({ clicked }) => ({
  display: "flex",
  height: "12px",
  width: "12px",

  // ANIMATION
  animation: clicked ? "upwards 0.5s forwards" : "downwards 0.5s forwards" /* Animation */,
  "@keyframes upwards": {
    " 0%": { transform: "rotate(0deg);" },
    "100% ": { transform: "rotate(180deg)" },
  },

  "@keyframes downwards": {
    " 0%": { transform: "rotate(180deg);" },
    "100% ": { transform: "rotate(0deg)" },
  },
}));

interface TimePeriodDropdownProps {
  options: ITimePeriodOption[];
  timePeriod: string;
  onTimePeriodChange: (newopt: ITimePeriodOption) => void;
  isHowTo?: boolean;
}

const TimePeriodDropdown = (props: TimePeriodDropdownProps) => {
  const { options, timePeriod, onTimePeriodChange, isHowTo } = props;
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const handleToggle = (): void => setIsOpen((prev): boolean => !prev);

  const handleOptionClick = (e: React.MouseEvent<HTMLDivElement>, opt: ITimePeriodOption) => {
    e?.stopPropagation();
    onTimePeriodChange(opt);
    handleToggle();
  };

  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    // Function to handle click outside
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Clicked outside the dropdown, close it
        setIsOpen(false);
      }
    };

    // Attach the event listener when the dropdown is open
    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    // Remove the event listener when the dropdown is closed or component unmounts
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <TimePeriodDropdownWrapper ref={dropdownRef} onClick={handleToggle} clicked={isOpen}>
      <>
        <DropdownTitle clicked={isOpen}>{capitalize(timePeriod)}</DropdownTitle>
        <DropdownArrowIcon clicked={isOpen}>
          <DownArrowIcon clicked={isOpen} />
        </DropdownArrowIcon>
        <DropdownContentWrapper>
          {isOpen && (
            <DropdownContent
              width={isHowTo ? "160px" : "110px"}
              top={isHowTo ? 110 : 126}
              left={isHowTo ? -90 : -5}>
              {options &&
                options.map((option, index) => (
                  <ContentOption
                    key={option.value}
                    onClick={(e: React.MouseEvent<HTMLDivElement>): void =>
                      handleOptionClick(e, option)
                    }>
                    {capitalize(option.name)}
                  </ContentOption>
                ))}
            </DropdownContent>
          )}
        </DropdownContentWrapper>
      </>
    </TimePeriodDropdownWrapper>
  );
};

export default TimePeriodDropdown;
